/***
 *
 *   USERS
 *   UsersSection / TeamUsersTable chrome over the shared DataTable. Invite /
 *   remove / revoke stay on ViewContext.modal with destructive confirms.
 *
 *   Members and pending invitations are one list here, as in the reference.
 *   They used to be told apart by comparing a translated badge string, which
 *   meant the row actions broke in any locale whose label differed; rows now
 *   carry a `kind` key and the copy is derived from it.
 *
 **********/

import React, { useContext, useMemo, useState } from 'react';
import {
    ViewContext,
    Card,
    SettingsWrapper,
    DataTable,
    Badge,
    Button,
    Animate,
    Icon,
    Menu,
    UserAvatar,
    usePermissions,
    useNavigate,
    useProjectContext,
    EmptyState,
} from 'components/lib';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import { useMutation } from 'react-query';
import { useUsers } from '../../../components/hooks/users';
import { formatDate } from 'utilities/date';
import Style from './users.tailwind';

const PERMISSION_COLOR = {
    owner: 'orange',
    admin: 'orange',
    developer: 'blue',
    user: 'neutral',
    contact: 'neutral',
};

function fullName(row) {
    return [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
}

function memberLabel(row) {
    return row.kind === 'invited' ? row.email : fullName(row) || row.email;
}

export function Users() {
    const { _t } = useTranslate();
    const context = useContext(ViewContext);
    const permissions = usePermissions();
    const navigate = useNavigate();
    const { projects } = useProjectContext();
    const usersData = useUsers(null);
    const { postEvent, callApi } = useApiContext();

    const [statusFilter, setStatusFilter] = useState('');
    const [permissionFilter, setPermissionFilter] = useState('');

    const { mutate: resendInvite } = useMutation(
        async (data) => {
            await callApi({
                url: '/api/invite?target=user',
                method: 'post',
                data: { email: data.email },
            });
        },
        {
            onSuccess: (data, variables) => {
                context.notification.show(
                    _t('invite.reSendTo', { email: variables.email }),
                    'success',
                    true,
                );
            },
            onError: (err) => {
                context.handleError(err);
            },
        },
    );

    function invite() {
        context.modal.show(
            {
                title: _t('user.add.one'),
                form: {
                    email: {
                        label: _t('table.email'),
                        type: 'text',
                        required: true,
                    },
                    permission: {
                        label: _t('permission.label'),
                        type: 'select',
                        default: 'user',
                        options: permissions?.data?.list
                            ?.map((x) => ({ value: x.value, label: _t(`permissions.${x.value}`) }))
                            .filter((x) => x.value !== 'owner'),
                    },
                    project_id: {
                        label: _t('project.label.one'),
                        type: 'select',
                        options: projects?.map((x) => ({ value: x.id, label: x.name })),
                    },
                },
                buttonText: _t('invite.send'),
                text: _t('invite.sendInfo'),
                url: '/api/invite?target=user',
                method: 'POST',
            },
            () => {
                usersData.refetch();
                postEvent('invited_user');
            },
        );
    }

    function editUser(user) {
        navigate(`/app/settings/users/${user.id}`, {
            state: { title: user.first_name },
        });
    }

    function deleteUser(data) {
        context.modal.show(
            {
                title: _t('user.delete.label'),
                form: {},
                buttonText: _t('user.delete.label'),
                text: _t('user.delete.confirm', { name: memberLabel(data), email: data.email }),
                url: `/api/user/${data.id}/organization`,
                method: 'DELETE',
                destructive: true,
            },
            () => {
                context.notification.show(_t('message.wasDeleted'), 'success', true);
                usersData.refetch();
            },
        );
    }

    function deleteInvite(data) {
        context.modal.show(
            {
                title: _t('invite.delete.label'),
                form: {},
                buttonText: _t('invite.delete.label'),
                text: _t('invite.delete.confirm', { email: data.email }),
                url: `/api/invite/${data.id}`,
                method: 'DELETE',
                destructive: true,
            },
            () => {
                context.notification.show(`${_t('invite.wasDeleted')}`, 'success', true);
                usersData.refetch();
            },
        );
    }

    const rows = useMemo(() => {
        const members = (usersData.data?.data.users || []).map((x) => ({
            kind: 'registered',
            id: x.id,
            first_name: x.first_name,
            last_name: x.last_name,
            email: x.email,
            permission: x.permission,
            activity: x.last_active || x.date_created,
        }));

        const invites = (usersData.data?.data.usersInvites || []).map((x) => ({
            kind: 'invited',
            id: x.id,
            first_name: '',
            last_name: '',
            email: x.email,
            permission: x.permission || 'user',
            activity: x.date_sent,
        }));

        // The owner manages the organisation rather than belonging to the
        // manageable list, and has no row action that would apply.
        return [...members, ...invites].filter((x) => x.permission !== 'owner');
    }, [usersData.data]);

    const visibleRows = useMemo(
        () =>
            rows.filter(
                (row) =>
                    (!statusFilter || row.kind === statusFilter) &&
                    (!permissionFilter || row.permission === permissionFilter),
            ),
        [rows, statusFilter, permissionFilter],
    );

    const permissionOptions = useMemo(
        () =>
            (permissions?.data?.list || [])
                .filter((x) => x.value !== 'owner')
                .map((x) => ({ value: x.value, label: _t(`permissions.${x.value}`) })),
        [permissions?.data?.list, _t],
    );

    function rowActions(row) {
        if (row.kind === 'invited')
            return [
                {
                    label: _t('table.actions.resendInvite'),
                    icon: 'refresh-cw',
                    action: () => resendInvite(row),
                },
                { separator: true },
                {
                    label: _t('invite.delete.label'),
                    icon: 'trash-2',
                    action: () => deleteInvite(row),
                },
            ];

        return [
            { label: _t('edit'), icon: 'edit-2', action: () => editUser(row) },
            { separator: true },
            { label: _t('user.delete.label'), icon: 'trash-2', action: () => deleteUser(row) },
        ];
    }

    const StatusBadge = ({ row }) => (
        <Badge
            text={_t(`table.badge.${row.kind}`)}
            color={row.kind === 'registered' ? 'green' : 'blue'}
            className={Style.badge}
        />
    );

    const PermissionBadge = ({ row }) => (
        <Badge
            text={_t(`permissions.${row.permission}`)}
            color={PERMISSION_COLOR[row.permission] || 'neutral'}
            className={Style.badge}
        />
    );

    /* The mobile card is itself a row target, so the menu stops the event.

       `side` flips for the last rows: the table wrapper is a scroll container,
       so a menu that opens downwards off the final row gets clipped by it. */
    const ActionsCell = ({ row, meta }) => (
        <span onClick={(e) => e.stopPropagation()}>
            <Menu
                align="end"
                side={meta?.count > 3 && meta.index >= meta.count - 2 ? 'top' : 'bottom'}
                width="w-52"
                label={_t('table.actions.more')}
                triggerClassName={Style.menuTrigger}
                trigger={<Icon image="more-horizontal" size={16} color="currentColor" />}
                list={rowActions(row)}
            />
        </span>
    );

    const columns = useMemo(
        () => [
            {
                key: 'member',
                header: _t('table.member'),
                sortable: true,
                sortValue: (r) => memberLabel(r).toLowerCase(),
                searchValue: (r) => `${fullName(r)} ${r.email}`,
                render: (r) => (
                    <div className={Style.identity}>
                        {r.kind === 'invited' ? (
                            <span className={Style.inviteMark}>
                                <Icon image="mail" size={14} color="currentColor" />
                            </span>
                        ) : (
                            <UserAvatar name={fullName(r) || r.email} size="sm" />
                        )}
                        <span className={Style.identityName}>{memberLabel(r)}</span>
                    </div>
                ),
            },
            {
                key: 'email',
                header: _t('table.email'),
                sortable: true,
                render: (r) => <span className={Style.secondary}>{r.email}</span>,
            },
            {
                key: 'permission',
                header: _t('table.permission'),
                sortable: true,
                width: 140,
                sortValue: (r) => _t(`permissions.${r.permission}`),
                searchValue: (r) => _t(`permissions.${r.permission}`),
                render: (r) => <PermissionBadge row={r} />,
            },
            {
                key: 'status',
                header: _t('table.status'),
                sortable: true,
                width: 120,
                sortValue: (r) => _t(`table.badge.${r.kind}`),
                searchValue: (r) => _t(`table.badge.${r.kind}`),
                render: (r) => <StatusBadge row={r} />,
            },
            {
                key: 'activity',
                header: _t('table.lastActivity'),
                sortable: true,
                width: 150,
                sortValue: (r) => (r.activity ? new Date(r.activity).getTime() : null),
                searchable: false,
                render: (r) => (
                    <span className={Style.muted}>
                        {formatDate(r.activity) || '—'}
                    </span>
                ),
            },
            {
                key: 'actions',
                header: <span className="sr-only">{_t('table.action')}</span>,
                actions: true,
                render: (r, meta) => <ActionsCell row={r} meta={meta} />,
            },
        ],
        [_t],
    );

    const renderCard = (row, meta) => (
        <div className={Style.cardRow}>
            {row.kind === 'invited' ? (
                <span className={Style.inviteMark}>
                    <Icon image="mail" size={14} color="currentColor" />
                </span>
            ) : (
                <UserAvatar name={fullName(row) || row.email} size="sm" />
            )}

            <div className={Style.cardBody}>
                <p className={Style.identityName}>{memberLabel(row)}</p>
                {row.kind !== 'invited' && <p className={Style.cardEmail}>{row.email}</p>}
                <div className={Style.cardMeta}>
                    <PermissionBadge row={row} />
                    <StatusBadge row={row} />
                </div>
                {row.activity && (
                    <p className={Style.cardActivity}>{formatDate(row.activity)}</p>
                )}
            </div>

            <ActionsCell row={row} meta={meta} />
        </div>
    );

    return (
        <SettingsWrapper>
            <Animate>
                <div className={Style.stack}>
                    <div className={Style.header}>
                        <h1 className={Style.title}>{_t('user.manage.label.multiple')}</h1>
                        <p className={Style.description}>{_t('user.inviteOrganization')}</p>
                    </div>

                    <Card>
                        <DataTable
                            search
                            searchPlaceholder={_t('user.search')}
                            rows={visibleRows}
                            columns={columns}
                            loading={usersData.isLoading}
                            rowKey={(r) => `${r.kind}-${r.id}`}
                            onRowClick={editUser}
                            isRowClickable={(r) => r.kind === 'registered'}
                            renderCard={renderCard}
                            defaultSort={{ key: 'member', dir: 'asc' }}
                            actions={
                                <Button
                                    variant="accent"
                                    size="sm"
                                    icon="user-plus"
                                    text={_t('user.add.one')}
                                    action={invite}
                                    className={Style.inviteBtn}
                                />
                            }
                            filters={[
                                {
                                    key: 'status',
                                    label: _t('table.status'),
                                    value: statusFilter,
                                    onChange: setStatusFilter,
                                    options: [
                                        { value: '', label: _t('table.filter.allStatuses') },
                                        { value: 'registered', label: _t('table.badge.registered') },
                                        { value: 'invited', label: _t('table.badge.invited') },
                                    ],
                                },
                                {
                                    key: 'permission',
                                    label: _t('table.permission'),
                                    value: permissionFilter,
                                    onChange: setPermissionFilter,
                                    options: [
                                        { value: '', label: _t('table.filter.allPermissions') },
                                        ...permissionOptions,
                                    ],
                                },
                            ]}
                            onResetFilters={() => {
                                setStatusFilter('');
                                setPermissionFilter('');
                            }}
                            empty={
                                <EmptyState
                                    bare
                                    icon="users"
                                    title={_t('user.label.multiple')}
                                    text={_t('user.inviteOrganization')}
                                    buttonText={_t('user.add.one')}
                                    action={invite}
                                />
                            }
                        />
                    </Card>
                </div>
            </Animate>
        </SettingsWrapper>
    );
}
